import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FileText, CheckCircle, AlertTriangle, 
  Brain, ScanLine, Share2, Download, ShieldCheck, 
  ArrowLeft, Search
} from 'lucide-react';
import { motion } from 'framer-motion';

import { Button } from '../components/ui/button';
import HeaderActions from '../components/HeaderActions';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const AnalysisResultPage = () => {
  const { t } = useLanguage(); // Dil fonksiyonunu çağır
  const location = useLocation();
  const navigate = useNavigate();
  const { document } = location.state || {};

  // Eğer belge verisi yoksa dashboard'a yönlendir (Crash olmasın)
  if (!document) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0B1120] text-white space-y-6">
        <div className="relative">
            <ScanLine size={64} className="text-blue-500 animate-pulse" />
            <div className="absolute inset-0 bg-blue-500/20 blur-xl rounded-full"></div>
        </div>
        <p className="text-blue-200/60 font-mono text-xs uppercase tracking-widest">{t('error_generic') || 'Data stream interrupted. Returning to hub...'}</p>
        {setTimeout(() => navigate('/dashboard'), 2000)}
      </div>
    );
  }

  const riskLevel = document.ai_analysis?.riskScore || 0;
  const isHighRisk = riskLevel > 50;

  return (
    // Global Arka Plan (#0B1120)
    <div className="min-h-screen bg-transparent text-slate-100 font-sans pb-32 transition-colors duration-500 flex flex-col relative overflow-hidden">
      
      {/* Background Glows */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-blue-900/10 rounded-full blur-[120px] pointer-events-none z-0"></div>

      {/* HEADER - Soft Glass */}
      <header className="sticky top-0 z-40 bg-[#0B1120]/80 backdrop-blur-xl border-b border-white/5 px-6 py-4">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
           <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-xl transition-colors text-slate-400 hover:text-white group">
                  <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
              </button>
              <div className="p-2.5 bg-gradient-to-br from-blue-600 to-blue-500 rounded-xl shadow-lg shadow-blue-600/20">
                  <Brain size={22} className="text-white" />
              </div>
              <div>
                  <h1 className="text-xl font-black tracking-tighter uppercase italic text-white leading-none">
                    Fox<span className="text-blue-500">Intel</span>
                  </h1>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                    AI Analysis Report • ID: {document.id ? document.id.slice(0, 8) : 'UNK'}
                  </p>
              </div>
           </div>
           <HeaderActions />
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto p-6 md:p-10 space-y-8 w-full relative z-10">
        
        {/* SUMMARY CARD */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
           
           {/* LEFT: PREVIEW & STATUS - Glass Panel */}
           <div className="lg:col-span-1 space-y-6">
              <div className="relative aspect-[3/4] glass-panel rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl group cursor-pointer">
                 {document.fileUrl ? (
                    <img src={document.fileUrl} alt="Doc" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
                 ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 bg-[#111827]">
                        <FileText size={48} className="mb-4 opacity-50"/>
                        <span className="text-[10px] font-bold uppercase tracking-widest">{t('no_documents_found')}</span>
                    </div>
                 )}
                 <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-transparent to-transparent opacity-90"></div>
                 
                 {/* Metadata Overlay */}
                 <div className="absolute bottom-6 left-6 right-6">
                    <div className="flex justify-between items-end">
                        <div>
                            <p className="text-blue-400 font-black uppercase tracking-widest text-[9px] mb-1">{t('doc_name') || 'Source File'}</p>
                            <p className="text-white text-xs font-mono truncate max-w-[180px]">{document.fileName || 'unknown_scan.pdf'}</p>
                        </div>
                        <button className="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors backdrop-blur-md border border-white/10">
                            <Search size={14}/>
                        </button>
                    </div>
                 </div>
              </div>

              {/* Risk Status Card */}
              <div className={`p-6 rounded-[2rem] border relative overflow-hidden ${isHighRisk ? 'bg-rose-500/5 border-rose-500/20' : 'bg-emerald-500/5 border-emerald-500/20'}`}>
                 <div className={`absolute top-0 right-0 p-8 opacity-5 pointer-events-none ${isHighRisk ? 'text-rose-500' : 'text-emerald-500'}`}>
                     {isHighRisk ? <AlertTriangle size={100} /> : <ShieldCheck size={100} />}
                 </div>
                 <div className="relative z-10">
                     <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-lg ${isHighRisk ? 'bg-rose-500/10 text-rose-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                            {isHighRisk ? <AlertTriangle size={18} /> : <ShieldCheck size={18} />}
                        </div>
                        <h3 className={`text-base font-black uppercase tracking-tight ${isHighRisk ? 'text-rose-400' : 'text-emerald-400'}`}>
                           {isHighRisk ? 'Audit Risk Detected' : 'Compliance Verified'}
                        </h3>
                     </div>
                     <p className="text-xs font-medium text-slate-400 leading-relaxed">
                        {isHighRisk 
                           ? 'AI has detected inconsistencies. Please review manually.' 
                           : t('gdpr_compliant') || 'Document is compliant and securely archived.'}
                     </p>
                 </div>
              </div>
           </div>

           {/* RIGHT: DATA EXTRACTION - Glass Panel */}
           <div className="lg:col-span-2 glass-panel rounded-[2.5rem] border border-white/5 shadow-2xl overflow-hidden flex flex-col">
              <div className="p-8 md:p-10 space-y-10 flex-1">
                 
                 {/* Top Row: Merchant & Amount */}
                 <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-8 border-b border-white/5">
                    <div>
                       <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2">{t('firm_data') || 'Merchant'}</p>
                       <h2 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight">{document.merchant_name || "Unknown Merchant"}</h2>
                    </div>
                    <div className="text-left md:text-right">
                       <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2">{t('amount') || 'Total Amount'}</p>
                       <div className="inline-flex items-baseline gap-1">
                           <span className="text-4xl font-mono font-black text-blue-400">{new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2 }).format(document.amount || 0)}</span>
                           <span className="text-xl font-bold text-slate-500">€</span>
                       </div>
                    </div>
                 </div>

                 {/* Data Grid */}
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                    <DataPoint label={t('category') || 'Category'} value={document.category || t('cat_general')} icon={<ScanLine size={14}/>} />
                    <DataPoint label={t('date') || 'Date'} value={new Date(document.date).toLocaleDateString()} icon={<ScanLine size={14}/>} />
                    <DataPoint label="Tax Rate" value="%19 (DE)" icon={<ScanLine size={14}/>} />
                    <DataPoint label="Status" value={t('paid') || "Processed"} icon={<CheckCircle size={14}/>} color="text-emerald-400"/>
                 </div>

                 {/* AI Summary Box */}
                 <div className="bg-[#111827]/50 rounded-2xl p-6 border border-white/5 relative group">
                    <div className="absolute -left-1 top-6 h-8 w-1 bg-purple-500 rounded-r-full"></div>
                    <h4 className="text-xs font-black uppercase tracking-widest text-purple-400 mb-3 flex items-center gap-2">
                       <Brain size={14}/> {t('analysis_summary') || 'AI Insight'}
                    </h4>
                    <p className="text-sm text-slate-300 leading-relaxed font-medium italic">
                       "{document.descriptionKey || document.summary || t('processing') || "AI analysis in progress..."}"
                    </p>
                 </div>
              </div>

              {/* Action Footer */}
              <div className="p-6 bg-white/[0.02] border-t border-white/5 flex flex-col md:flex-row gap-4">
                 <Button onClick={() => navigate('/archive')} className="flex-1 h-14 bg-white text-[#0B1120] hover:bg-blue-50 rounded-xl font-black uppercase tracking-widest text-xs transition-all shadow-lg active:scale-95">
                    {t('save_changes') || 'Confirm & Archive'}
                 </Button>
                 <div className="flex gap-2">
                    <Button variant="outline" className="h-14 w-14 rounded-xl border-white/10 bg-transparent hover:bg-white/5 text-slate-400 hover:text-white transition-colors">
                       <Share2 size={20} />
                    </Button>
                    <Button variant="outline" className="h-14 w-14 rounded-xl border-white/10 bg-transparent hover:bg-white/5 text-slate-400 hover:text-white transition-colors">
                       <Download size={20} />
                    </Button>
                 </div>
              </div>

           </div>
        </motion.div>
      </main>
    </div>
  );
};

// Helper Component for Data Points
const DataPoint = ({ label, value, icon, color = "text-white" }) => (
  <div className="space-y-1.5">
     <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
        {icon} {label}
     </p>
     <p className={`text-sm font-bold ${color} truncate`}>{value || '-'}</p>
  </div>
);

export default AnalysisResultPage;